import * as React from 'react';

export type AuthType = 'b2e' | 'b2c';

/**
 * User data extracted from JWT token
 * Roles are NOT included - fetched from organizations API
 *
 * firstName and lastName are REQUIRED:
 * - If token doesn't have these fields, authentication fails
 * - We don't support "user with no name" - that's an invalid state
 */
export interface TokenData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isInternalUser: boolean;
}

/**
 * Single source of truth for what the app should render:
 * - initializing: MSAL/session validation in progress (loader)
 * - authenticated: valid session (app)
 * - unauthenticated: no usable cached credentials (landing / login redirect)
 * - sessionExpired: credentials were cached but can no longer be renewed (session-expired screen)
 */
export type AuthStatus =
  | 'initializing'
  | 'authenticated'
  | 'unauthenticated'
  | 'sessionExpired'
  | 'error';

/** Result of validating the cached session; `sessionExpired` comes from the session-expired store. */
export type AuthState =
  | { status: 'initializing' }
  | { status: 'unauthenticated' }
  | { status: 'error' }
  | { status: 'authenticated'; authData: TokenData };

export interface AuthContextValue {
  status: AuthStatus;
  authData: TokenData | null;
  /** Derived: status === 'authenticated' */
  isAuthenticated: boolean;
  /** Derived: status === 'initializing' */
  isLoading: boolean;
  retry: () => void;
  login: (type: AuthType) => Promise<void>;
  logout: () => Promise<void>;
}

export interface AuthProviderProps {
  readonly children: React.ReactNode;
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined
);
